<script setup lang="ts">
import {
  WdsHeading,
  WdsIcon,
  WdsIconNameEnum,
  WdsLabel,
  WdsModal,
  WdsStack,
} from '@worksome/wds-components'
import { useMutation } from '@vue/apollo-composable'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { UpdateWorksomeIntelligenceConsentMutation } from '@/components/LLM/graphql/mutations'
import { NetworkError, Notification } from '@/utils/notification'
import enums from '@/utils/enum'

const emit = defineEmits<{
  'ai-terms-agreement-agreed': []
  'ai-terms-agreement-declined': []
  'ai-terms-agreement-error': []
}>()

const { show = false } = defineProps<{ show: boolean }>()

const { mutate: updateWorksomeIntelligenceConsentMutation } = useMutation(
  UpdateWorksomeIntelligenceConsentMutation,
  {
    clientId: 'publicApi',
  },
)

const handleAiConsent = async (consent: boolean) => {
  try {
    await updateWorksomeIntelligenceConsentMutation({
      input: {
        consent,
      },
    })
  } catch (e: any) {
    emit('ai-terms-agreement-error')

    // Simplify error handling
    if (e.graphQLErrors?.length > 0) {
      new Notification({
        msg: e.graphQLErrors[0].message,
        type: enums.notification.types.ERROR,
        severity: enums.notification.severity.CRITICAL,
        config: { timeout: 5000 },
      }).dispatch()
    } else {
      new NetworkError({
        severity: enums.notification.severity.CRITICAL,
        ex: e,
      }).dispatch()
    }

    return
  }

  // Emit the appropriate event based on the answer
  if (consent) {
    emit('ai-terms-agreement-agreed')
  } else {
    emit('ai-terms-agreement-declined')
  }
}
</script>

<template>
  <WdsModal
    :show="show"
    md
    class="ai-terms-modal !tw-overflow-y-hidden"
    @cancel="handleAiConsent(false)"
  >
    <template #header>
      <WdsStack gap-sm align-center>
        <WdsIcon :name="WdsIconNameEnum.WORKSOME_ASSISTANT_GRADIENT" />
        <WdsHeading class="tw-my-0" type="title2">
          {{ $t('Worksome Intelligence') }}
        </WdsHeading>
        <div>
          <WdsLabel xs warning subtle>
            {{ $t('Beta') }}
            <WdsIcon
              :size="13"
              weight="thin"
              :name="WdsIconNameEnum.LIGHT"
              class="!tw-inline-block"
            />
          </WdsLabel>
        </div>
      </WdsStack>
    </template>

    <div class="tw-max-h-[70vh] tw-overflow-y-auto">
      <slot name="terms">
        <p><strong>Experience Worksome Intelligence Beta</strong></p>
        <p>
          We're excited to have you try our AI-powered Beta!
          <br />
          It's designed to enhance your workflow, but since it's still evolving, we want you to be
          aware of a few key points:
        </p>
        <ul>
          <li>
            The Beta is provided
            <strong>as is</strong>
            , with no guarantees. We may improve, change, or remove it anytime.
          </li>
          <li>
            <strong>Don't rely on it</strong>
            for critical business decisions just yet.
          </li>
        </ul>
        <p><strong>Liability &amp; Data</strong></p>
        <ul>
          <li>
            Our liability is
            <strong>limited</strong>
            —see the
            <a
              href="https://storage.worksome.com/platform/files/Worksome-Beta-User-Agreement_20250224_v1.0.pdf"
              target="_blank"
            >
              Beta Terms
            </a>
            .
          </li>
          <li>
            Data may be processed by third parties.
            <strong>Don't submit sensitive info</strong>
            unless you have proper consent.
          </li>
        </ul>
        <p>
          Learn more about
          <a href="https://worksome.notion.site/worksome-intelligence" target="_blank">
            how Worksome uses AI
          </a>
          .
        </p>
        <p>
          By clicking
          <strong>“I Agree”</strong>
          , you accept the
          <a
            href="https://storage.worksome.com/platform/files/Worksome-Beta-User-Agreement_20250224_v1.0.pdf"
            target="_blank"
          >
            Beta Terms
          </a>
          .
        </p>
      </slot>
      <div class="tw-flex tw-justify-center tw-mt-[20px]">
        <button type="button" class="btn btn-primary" @click="handleAiConsent(true)">
          I agree
        </button>
      </div>
    </div>
  </WdsModal>
</template>

<style scoped lang="scss">
.ai-terms-modal {
  :deep(.wds-modal-header) {
    @apply tw-py-6 tw-px-6 !important;
    @apply tw-border-b !important;
    @apply tw-border-gray-200 !important;
  }
}
</style>
